import { queryOptions } from '@tanstack/react-query';

import type {
  GetPlansParams,
  GetPlansResponse,
  Plan,
  ArchivePlansRequest,
  ArchivePlansResponse,
  ConfirmPlansRequest,
  ConfirmPlansResponse,
  PlanIdentifier,
  CreateFullPlanRequest,
  UpdateFullPlanRequest,
} from '../types/plan.types';

import apiClient from '@/axios/app-client';

const PLANS_ENDPOINT = 'v1/plans';

/**
 * Get plans with filtering and pagination
 * Map backend fields to frontend Plan type if needed
 */
export const getPlans = async (params: GetPlansParams = {}): Promise<GetPlansResponse> => {
  const response = await apiClient.get<GetPlansResponse>(PLANS_ENDPOINT, params);
  // The API client wraps response in ApiResponse format, so we access the nested data
  return response.data.data;
};

/**
 * Get plans query options for React Query
 */
export const getPlansQueryOptions = (params: GetPlansParams = {}) =>
  queryOptions({
    queryKey: ['plans', params],
    queryFn: () => getPlans(params),
  });

/**
 * Get plan by ID
 */
export const getPlanById = async (id: string, hasPast: boolean, hasArchive: boolean) => {
  const response = await apiClient.get(`${PLANS_ENDPOINT}/${id}`, {
    hasPast,
    hasArchive,
  });
  return response.data.data;
};

/**
 * Create plan
 */
export const createPlan = async (planRequest: CreateFullPlanRequest) => {
  const response = await apiClient.post<Plan>(`${PLANS_ENDPOINT}`, planRequest);
  return response.data.data;
};

/**
 * Update plan
 */
export const updatePlanById = async (planId: string, planRequest: UpdateFullPlanRequest) => {
  const response = await apiClient.put<Plan>(`${PLANS_ENDPOINT}/${planId}`, planRequest);
  return response.data.data;
};

/**
 * Get plan query options for React Query
 */
export const getPlanQueryOptions = (id: string, hasPast: boolean, hasArchive: boolean) =>
  queryOptions({
    queryKey: ['plan', id],
    queryFn: () => getPlanById(id, hasPast, hasArchive),
    enabled: !!id,
  });

/**
 * Archive or restore multiple plan deliveries
 * @param planIdentifiers - Array of plan identifiers (id)
 * @param isArchive - true to archive, false to restore
 */
export const archivePlans = async (
  planIdentifiers: PlanIdentifier[],
  isArchive: boolean
): Promise<ArchivePlansResponse> => {
  const requestBody: ArchivePlansRequest = {
    planLists: planIdentifiers,
    isArchive,
  };

  const response = await apiClient.put<ArchivePlansResponse>(
    `${PLANS_ENDPOINT}/archive`,
    requestBody
  );
  return response.data.data;
};

/**
 * Confirm or unconfirm multiple plan deliveries the day before
 * @param planIdentifiers - Array of plan identifiers (id)
 * @param isConfirm - true to confirm, false to unconfirm
 */
export const confirmPlans = async (
  planIdentifiers: PlanIdentifier[],
  isConfirm: boolean
): Promise<ConfirmPlansResponse> => {
  const requestBody: ConfirmPlansRequest = {
    planLists: planIdentifiers,
    isConfirm,
  };

  const response = await apiClient.put<ConfirmPlansResponse>(
    `${PLANS_ENDPOINT}/confirm`,
    requestBody
  );
  return response.data.data;
};
